import { Alert, Button, Chip, Paper, Stack, Typography } from "@mui/material";

import { EmptyState, Loading } from "@lopie/kernel-ui";

import { alertLine, scheduleLine, sharingLine } from "../lib/reports.js";
import { describe, describeMeasure } from "../lib/spec.js";

/** What an alert on this report watches: the first thing it counts or adds up. */
export const figureOf = (r, schema) => describeMeasure(r.state?.measures?.[0] || { fn: "count", field: "" }, schema, r.state?.type);
/** An alert watches one figure, so a grouped report has none to watch. */
export const alertable = (r) => !(r.state?.dimensions || []).some((d) => d.field);

/**
 * Reports the person saved, and ones that were shared with them.
 *
 * They are in the kernel, so they are the same on every browser and can be
 * shared. A saved report holds the question and never the answer: opening one
 * asks the kernel again, as the person who opened it, so what comes back is what
 * *that* person may open. The owner can share it, see its versions and remove
 * it; somebody it was shared with can open it and keep a copy of their own.
 */
export default function Saved({
  list, schema, loading, failed, local, onOpen, onShare, onHistory, onSchedule, onAlert, onDelete, onCopy, onMove, moving,
}) {
  return (
    <Stack sx={{ gap: 2 }}>
      {failed ? (
        <Alert severity="warning" role="alert">Your reports could not be read from the kernel just now, so this list may be incomplete.</Alert>
      ) : null}
      {local?.count ? (
        <Alert
          severity="info" variant="outlined"
          action={<Button color="inherit" size="small" disabled={moving} onClick={onMove}>{moving ? "Moving…" : "Move them"}</Button>}
        >
          {local.count} saved in this browser from before reports were kept in the kernel. Move them to keep them
          with your account and share them. They stay here until the kernel has them.
        </Alert>
      ) : null}
      {loading ? (
        <Loading label="Reading your reports" minHeight={120} />
      ) : list.length === 0 ? (
        <EmptyState title="Nothing saved yet">
          <Typography variant="body2">
            Build a chart, give it a name and press Save, and it will be here for you and anyone you share it with.
          </Typography>
        </EmptyState>
      ) : (
        list.map((r) => (
          <Paper key={r.id} variant="outlined" sx={{ p: 2 }} component="section" aria-label={r.name}>
            <Stack direction="row" sx={{ gap: 2, justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
              <Stack sx={{ minWidth: 0, gap: 0.25 }}>
                <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{r.name}</Typography>
                  <Chip size="small" variant="outlined" label={sharingLine(r)} />
                </Stack>
                <Typography variant="body2" color="text.secondary">{describe(r.state, schema)}</Typography>
                {r.schedule ? <Typography variant="body2">{scheduleLine(r.schedule)}.</Typography> : null}
                {(r.alerts || []).map((a) => (
                  <Typography key={a.id} variant="body2">{alertLine(a, figureOf(r, schema))}.</Typography>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Version {r.version} · {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : ""}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" size="small" onClick={() => onOpen(r)}>Open</Button>
                {r.role === "owner" ? (
                  <>
                    <Button size="small" onClick={() => onShare(r)} aria-label={`Share ${r.name}`}>Share</Button>
                    <Button size="small" onClick={() => onSchedule(r)} aria-label={`Email ${r.name}`}>Email</Button>
                    <Button
                      size="small" onClick={() => onAlert(r)} aria-label={`Alert on ${r.name}`}
                      disabled={!alertable(r)} title={alertable(r) ? undefined : "An alert watches one figure, so it needs a report that is not grouped."}
                    >
                      Alert
                    </Button>
                    <Button size="small" onClick={() => onHistory(r)} aria-label={`Versions of ${r.name}`}>Versions</Button>
                    <Button size="small" color="inherit" onClick={() => onDelete(r)} aria-label={`Remove ${r.name}`}>Remove</Button>
                  </>
                ) : (
                  <Button size="small" onClick={() => onCopy(r)} aria-label={`Keep a copy of ${r.name}`}>Keep a copy</Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );
}
